import clsx, { type ClassValue } from 'clsx';
import { format, type FormatOptions } from 'date-fns';
import { twMerge } from 'tailwind-merge';

/** Merge classes with tailwind-merge with clsx full feature */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format date to ISO string */
export function formatDateToISOString(date: string | number | Date): string {
  return format(new Date(date), "yyyy-MM-dd'T'HH:mm:ss.SSS");
}

/**
 * Return a string with a formatted date matching the pattern date (patterns coming from constants,
 * see full reference here https://date-fns.org/v2.22.1/docs/format)
 * @Example
 * ```
 * // Returns a string as 25 May 2021, 6:05 PM
 * formatDate(new Date('05-25-2021, 18:05:00'), DATE_TIME_WITH_YEAR_LONG)
 * formatDate(1621922735935, DATE_TIME_WITH_YEAR_LONG)
 * ```
 */
export function formatDate(
  date: Date | number | string | undefined,
  dateFormatPattern: string,
  options?: FormatOptions,
) {
  if (date) {
    return format(date, dateFormatPattern, options);
  }

  return '';
}

/** Extract a "HH:mm" (24h, zero-padded) time-of-day string from an epoch ms timestamp, using local time. */
export function timestampToTimeString(
  ms: number | null | undefined,
): string | null {
  if (!ms) return null;

  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Combine a date's epoch ms (e.g. an agenda day's local midnight) with a "HH:mm" time-of-day
 * string into a single epoch ms timestamp for that date at that time.
 */
export function combineDateAndTime(
  dateMs: number | null | undefined,
  time: string | null | undefined,
): number | null {
  if (dateMs == null || !time) return null;

  const [hStr, mStr] = time.split(':');
  const hours = Number(hStr);
  const minutes = Number(mStr);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const d = new Date(dateMs);
  d.setHours(hours, minutes, 0, 0);
  return d.getTime();
}

export function timeAgo(timestamp: number | string | Date): string | boolean {
  let date: Date;

  if (typeof timestamp === 'number') {
    date = new Date(timestamp * 1000);
  } else if (typeof timestamp === 'string') {
    const parsedTimestamp = Date.parse(timestamp);

    if (!isNaN(parsedTimestamp)) {
      date = new Date(parsedTimestamp);
    } else {
      const numTimestamp = parseFloat(timestamp);
      if (!isNaN(numTimestamp)) {
        date = new Date(numTimestamp * 1000);
      } else {
        throw new Error('Invalid date format');
      }
    }
  } else {
    date = timestamp;
  }

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format');
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  const twelveHoursInSeconds = 12 * 3600;

  if (diffSeconds > twelveHoursInSeconds) {
    return false;
  }

  if (diffSeconds < 60) {
    return `${diffSeconds} ${diffSeconds === 1 ? 'second' : 'seconds'} ago`;
  } else if (diffSeconds < 3600) {
    const diffMinutes = Math.floor(diffSeconds / 60);
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else {
    const diffHours = Math.floor(diffSeconds / 3600);
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }
}
